import { useEffect, useRef } from 'react';
import { Alert, AlertDescription } from '@/ui/shadcn/alert';
import { Button } from '@/ui/shadcn/button';
import { Icon } from '../../../ui';
import { useConsentRedirectBusy } from '../useConsentRedirectBusy';
import { focusIsLost } from '../useRefetchRestoringFocus';
import { connectFailureText } from './connectMarkers';
import { useOptionalMailboxConnect } from './mailboxConnectContext';

/**
 * *Your mailbox could not be connected*, on whatever CRM page the user is on when a connect fails (F20-i
 * spec FR-17, FR-19, NFR-6; Wave-1 contract § Redirect Contract, "Where the message shows"). `AppLayout`
 * renders it full width at the top of the page canvas, above the routed page and outside the Suspense
 * boundary, so it shows while a route chunk loads.
 *
 * - **One announcement** (F20-i WI-1.3 plan D-9): a visually hidden polite status line, mounted for the frame's
 *   whole life and empty until a failure, carries the message and the reason — a live region must exist
 *   before its words do. The visible `Alert` is not a live region (`role="none"`), so the text is read once.
 *   The status line alone is held empty while a start runs or after one was refused (`statusQuiet`); the
 *   banner keeps showing the message.
 * - **The banner** (F20-i WI-1.3 plan D-22, the lead's decision): warning tone — a recoverable problem, not a destructive one —
 *   with *Dismiss*. It stays until *Dismiss*, a successful connect (a *Try again* that fails replaces it, and it stays
 *   on screen while the retry runs), a back/forward-cache restore (`.forge/lessons.md` L-109) or the next page load
 *   (F20-i WI-1.3 plan D-11).
 * - **Its own *Try again*, except where the page shows its `nextStep` button** (F20-i WI-1.3 plan D-10, the lead's two-button
 *   rule): `NextStepAction` registers while it renders a button, and the banner then leaves its own out, so
 *   `/email/mailbox` and the Email screen show one *Try again*, the page's.
 *
 * Outside `MailboxConnectProvider` it renders nothing (F20-i WI-1.3 plan D-4: `AppLayout`'s own tests render the frame with
 * no provider).
 */
/** The banner's *Try again*, by its accessible name. */
export const BANNER_TRY_AGAIN = 'Try again: connect your mailbox';

export interface MailboxConnectBannerProps {
    /**
     * Called once the banner has left after a press of its own *Try again* or *Dismiss*, when focus was on
     * the banner or nowhere at the press and is lost now: the pressed button went with the banner, which sits
     * outside every page's focus handling, so `AppLayout` moves focus to the page heading (`.forge/lessons.md`
     * L-133). Focus the user moved elsewhere meanwhile is left alone.
     */
    readonly onGone?: () => void;
}

export function MailboxConnectBanner({ onGone }: MailboxConnectBannerProps = {}) {
    const connect = useOptionalMailboxConnect();
    // When `busy` clears is the shared consent-redirect rule (BUG-024).
    const redirect = useConsentRedirectBusy();
    const bannerRef = useRef<HTMLDivElement>(null);
    // A press of the banner's own button, made while focus was on the banner or nowhere, whose banner has
    // not left yet.
    const pressedHere = useRef(false);
    const shownFailure = connect?.failure ?? null;
    const shown = shownFailure !== null;
    const lastFailure = useRef(shownFailure);

    // A different failure while the banner stays (a failed retry) uses up the press: a later leave for another
    // reason (the page's own button) does not move focus. Only this and the leave below use it up — not the
    // start's promise settling, which can come before the banner has rendered its leave.
    useEffect(() => {
        const previous = lastFailure.current;
        lastFailure.current = shownFailure;
        if (shownFailure !== null && previous !== null && shownFailure !== previous) {
            pressedHere.current = false;
        }
    }, [shownFailure]);

    useEffect(() => {
        if (shown || !pressedHere.current) {
            return;
        }
        pressedHere.current = false;
        if (focusIsLost()) {
            onGone?.();
        }
    }, [shown, onGone]);

    if (connect === null) {
        return null;
    }
    const { failure, statusQuiet, nextStepButtonShown, startInteractive, dismissFailure } = connect;
    // What the banner shows, whenever there is a failure, and what the status line says: the same words, except
    // that the status line is held empty while `statusQuiet` (a start running, or one refused), so only the
    // announcement pauses and the banner never becomes a box holding only its buttons.
    const message = failure === null ? '' : connectFailureText(failure.reason);
    const announced = statusQuiet ? '' : message;

    /** Note, at the press, whether focus is on the banner or nowhere. */
    function notePress(): void {
        const active = document.activeElement;
        pressedHere.current =
            active === null || active === document.body || (bannerRef.current?.contains(active) ?? false);
    }

    function retry(): void {
        notePress();
        // `run` catches a refused start; the failure, its message and this button stay. The refusal itself is
        // not announced (stub path only: `pnpm dev` and Vitest), as `MailboxConnectValue.statusQuiet` says.
        void redirect.run(() => startInteractive());
    }

    return (
        <>
            <p
                aria-atomic="true"
                aria-live="polite"
                className="sr-only"
                data-testid="app-mailbox-connect-status"
                role="status"
            >
                {announced}
            </p>
            {failure === null ? null : (
                <Alert
                    ref={bannerRef}
                    className="mb-(--et-space-6) border-warning bg-warning-muted text-warning-muted-foreground"
                    data-testid="app-mailbox-connect-failed"
                    role="none"
                >
                    <Icon name="warning" size="sm" />
                    <AlertDescription className="text-warning-muted-foreground">
                        <p>{message}</p>
                        <div className="flex flex-wrap gap-(--et-space-3)">
                            {nextStepButtonShown ? null : (
                                <Button
                                    // The page under the banner can carry a *Try again* of its own (a list's read), so
                                    // this one names what it retries (`.forge/lessons.md` L-211).
                                    aria-label={BANNER_TRY_AGAIN}
                                    busy={redirect.busy}
                                    data-testid="app-mailbox-connect-try-again"
                                    onClick={retry}
                                    size="sm"
                                >
                                    Try again
                                </Button>
                            )}
                            <Button
                                aria-label="Dismiss"
                                data-testid="app-mailbox-connect-dismiss"
                                onClick={() => {
                                    notePress();
                                    dismissFailure();
                                }}
                                size="sm"
                                variant="ghost"
                            >
                                <Icon name="close" size="sm" />
                                Dismiss
                            </Button>
                        </div>
                    </AlertDescription>
                </Alert>
            )}
        </>
    );
}
